import React, { useCallback, useMemo } from 'react';
import { AppButton, Box, Inscription, Preloader, WarnModal } from '@common-fe/common-fe';

import { ContributionRequestStatus } from '../Contributions/contributions.types';

interface Props {
  visible: boolean;
  isApproval: boolean;
  errorCount?: number;
  isUpdating?: boolean;
  onClose: () => void;
  onSubmit: (status: ContributionRequestStatus) => void;
  onReviewErrors: () => void;
}

export const ContributionApprovalModal: React.FC<Props> = ({
  visible,
  isApproval,
  errorCount,
  isUpdating,
  onClose,
  onSubmit,
  onReviewErrors,
}) => {
  const hasErrors = useMemo(() => Boolean(errorCount), [errorCount]);

  const header = useMemo(() => {
    if (isApproval) {
      return hasErrors
        ? 'Errors were found, and your changes may not be applied unless they\'re corrected'
        : 'Approve contributions?';
    }
    return 'Reject contribution?';
  }, [isApproval, hasErrors]);

  const submitButtonText = useMemo(() => {
    if (isApproval) {
      return hasErrors ? 'Submit With Errors' : 'Yes, Approve';
    }
    return 'Yes, Reject';
  }, [isApproval, hasErrors]);

  const cancelButtonText = useMemo(() => {
    return hasErrors ? 'Review and Fix Errors' : 'Cancel';
  }, [hasErrors]);

  const buttonWidth = useMemo(() => {
    return hasErrors ? '180px' : '160px';
  }, [hasErrors]);

  const isSubmitDisabled = useMemo(() => {
    return isApproval && typeof errorCount === 'undefined';
  }, [isApproval, errorCount]);

  const handleSubmit = useCallback(() => {
    const status = isApproval
      ? ContributionRequestStatus.PROCESSING
      : ContributionRequestStatus.REJECTED;
    onSubmit(status);
  }, [isApproval, onSubmit]);

  const handleCancel = useCallback(() => {
    if (hasErrors) {
      onReviewErrors();
    }
    onClose();
  }, [hasErrors, onReviewErrors, onClose]);

  const errorMessage = useMemo(() => {
    if (!hasErrors) return null;
    
    return (
      <Box margin={{ top: 'spacing12' }}>
        <Inscription textAlign="center" data-testId="NumberOfErrors">
          We found{' '}
          <Inscription weight="bold">
            {errorCount} record{(errorCount || 0) > 1 ? 's' : ''}
          </Inscription>{' '}
          with errors. If you submit now, they may not be applied to the participant's account.
        </Inscription>
      </Box>
    );
  }, [hasErrors, errorCount]);

  if (!visible) {
    return null;
  }

  if (isSubmitDisabled) {
    return (
      <WarnModal
        testId="ContributionActivitiesList_warn_modal_loading"
        visible
        iconNode={<Box/>}
        onSetVisible={onClose}
      >
        <Preloader/>
      </WarnModal>
    );
  }

  return (
    <WarnModal
      testId="ContributionActivitiesList_warn_modal"
      visible
      header={header}
      onSetVisible={onClose}
      submitButtonNode={
        <AppButton
          testId="submitButton"
          width={buttonWidth}
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
        >
          {isUpdating ? (
            <Preloader size={16} thinSize={4} color="border2" />
          ) : (
            submitButtonText
          )}
        </AppButton>
      }
      cancelButtonNode={
        <AppButton
          testId="cancel"
          width={buttonWidth}
          buttonType="secondary"
          onClick={handleCancel}
        >
          {cancelButtonText}
        </AppButton>
      }
      isPendingIcon
    >
      {errorMessage}
    </WarnModal>
  );
};
